import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { STANDARD_FORM_NAME } from '../../../shared/builtin';
import { pageViewport, pdfRectToViewport } from '../../../shared/pdf/geometry';
import { placementOverlayStyle } from '../../pdf/render';
import { loadStandardForm, type StandardFormField } from '../../lib/standardForm';
import { PdfPageViewer, type ViewerPage } from './PdfPageViewer';
import { PdfCheckmark } from './PdfDocumentEditor';
import { PDF_FIELD_ICONS } from './PdfFieldList';
import { AppIcon } from '../AppIcon';
import { PDF_FIELD_TYPE_NAMES } from '../../../shared/pdfBindingValidation';

/**
 * The standard permission form, shown the way an organizer's own form is in Edit form, but only to look at: each of its
 * fields is outlined and numbered on the PDF with the answer that fills it, and listed in order below.
 */
export function StandardFormView({ onClose }: { onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null), titleId = useId();
  const [fields, setFields] = useState<StandardFormField[]>([]);
  // One loader for the dialog's life, so the viewer opens the PDF once.
  const load = useRef(async (signal: AbortSignal) => { const loaded = await loadStandardForm(signal); setFields(loaded.fields); return loaded.source; });
  useEffect(() => {
    const element = dialog.current; if (!element) return;
    if (typeof element.showModal === 'function') { if (!element.open) element.showModal(); } else element.setAttribute('open', '');
    window.document.documentElement.classList.add('form-dialog-open');
    return () => { window.document.documentElement.classList.remove('form-dialog-open'); if (element.open && typeof element.close === 'function') element.close(); };
  }, []);
  const size = (field: StandardFormField, view: ViewerPage) => {
    const scale = view.viewport.width / (pageViewport(view.source.pages[view.pageIndex]).width || view.viewport.width), box = pdfRectToViewport(view.viewport, field.rect);
    return { width: box.width / scale, height: box.height / scale };
  };
  return <dialog ref={dialog} class="pdf-form-dialog standard-form-view" aria-labelledby={titleId} onCancel={e => { e.preventDefault(); onClose(); }}>
    <header class="pdf-form-dialog-header">
      <div class="stack tight"><p class="eyebrow">Viewing a form</p><h2 id={titleId} tabIndex={-1} autofocus>{STANDARD_FORM_NAME}</h2></div>
    </header>
    <div class="stack pdf-form-dialog-body">
      <p>Every registration fills in this form. Each box on it shows what fills it: an answer from registration, a detail of your event, or a signature. A form you upload can be filled in the same way, from these answers or from questions of your own.</p>
      <PdfPageViewer eventId="" transcript={false} loadSource={load.current} overlay={view => fields.filter(f => f.pageIndex === view.pageIndex).map(f => {
        const number = fields.indexOf(f) + 1, tall = size(f, view).height > 20;
        return <div key={f.id} class={`pdf-placement native kind-${f.kind}`} style={placementOverlayStyle(view.viewport, f.rect)} title={`${number}. ${f.name}`}>
          <span class="pdf-placement-badge" aria-hidden="true">{number}</span>
          {f.kind === 'checkbox' ? <PdfCheckmark {...size(f, view)} />
            : <span class={`pdf-placement-name ${tall ? 'v-top multiline' : 'v-middle'}`} aria-hidden="true" style={{ '--font-size': 9 }}>{f.name}</span>}
        </div>;
      })} />
      {!!fields.length && <section class="stack standard-form-fields" aria-labelledby={`${titleId}-fields`}>
        <h3 id={`${titleId}-fields`}>Fields on this form</h3>
        {/* Numbered and marked by type as in Edit form's list of fields. */}
        <ol>{fields.map((f, index) => <li key={f.id} class="pdf-field-row">
          <span class="pdf-field-row-badge" aria-hidden="true">{index + 1}</span>
          <span class="pdf-field-row-type" title={PDF_FIELD_TYPE_NAMES[f.kind]}><AppIcon name={PDF_FIELD_ICONS[f.kind]} /><span class="sr-only">{PDF_FIELD_TYPE_NAMES[f.kind]}: </span></span>
          <span>{f.name}</span>
        </li>)}</ol>
      </section>}
    </div>
    <footer class="pdf-form-dialog-footer">
      <div class="row wrap pdf-form-dialog-actions"><button onClick={onClose}>Close</button></div>
    </footer>
  </dialog>;
}
